import * as React from 'react';
import * as O from 'fp-ts/lib/Option';
import * as IO from 'fp-ts/lib/IO';
import * as IOE from 'fp-ts/lib/IOEither';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import { sequenceT } from 'fp-ts/lib/Apply';

import { is } from './utils';



export type HasError = boolean;


// -------------------------------------------------------------------------------------
// Base props 
// -------------------------------------------------------------------------------------

export interface Position {
    readonly top: number,
    readonly left: number,
}

export interface Size {
    readonly width: number,
    readonly height: number,
}


// -------------------------------------------------------------------------------------
// Environments 
// -------------------------------------------------------------------------------------

export interface Refable<T extends HTMLElement> {
    readonly ref: React.RefObject<T>
}

export interface Showable {
    readonly show: boolean,
}

export interface Dispatchable<T> {
    readonly dispatch: React.Dispatch<React.SetStateAction<T>>,
}

export interface Windowable {
    readonly window: Window,
}

export interface Documentable {
    readonly document: Document,
}

export interface Errorable {
    readonly exception: () => void,
}


// -------------------------------------------------------------------------------------
// Errors
// -------------------------------------------------------------------------------------

export class NotWindow extends Error {}
export class NotDocument extends Error {}
export class NodeNotFound extends Error {}



export const emptySize = {
    width: 0,
    height: 0,
}

export const emptyPosition = {
    top: 0,
    left: 0,
}


/**
 * Чистое получение объекта window из окружения
 */
export const getWindow = (env: Windowable) => {
    return pipe(
        env,
        env => env.window ? E.right(env.window) : E.left(new NotWindow('Мы находимся не в окружении браузера!')),
        IOE.fromEither,
    );
}


export const safeGetNode = (ref: React.RefObject<HTMLElement>) => 
    pipe(
        ref,
        (r => r.current),
        E.fromNullable(new NodeNotFound('STC23')),
        IOE.fromEither,
    );


/**
 * Безопасное чтение элемента из окружения
 */
export const extractNode = <T extends HTMLElement>(env: Refable<T>) =>
    pipe(
        env.ref.current,
        E.fromNullable(new NodeNotFound('STC23')),
        IOE.fromEither,
    );

export const extractDocument = (env: Documentable) =>
    pipe(
        env.document,
        E.fromNullable(new NotDocument('STC23 not document')),
        IOE.fromEither,
    );


/**
 * Получить текущую прокрутку документа
 */
export const getScroll = (env: Windowable): IO.IO<Readonly<Position>> =>
    pipe(
        getWindow(env),
        IOE.map(w => ({
            left: w.pageXOffset,
            top: w.pageYOffset,
        })),
        IOE.fold(
            () => () => emptyPosition,
            (s) => () => s,
        ),
    );


/**
 * Получить координаты элемента относительно экрана
 * @param x - HTML элемент
 */
export const getScreenPosition = (x: HTMLElement): IO.IO<Readonly<Position>> => () => {
    const { top, left } = x.getBoundingClientRect();
    return { top, left };
}


/**
 * Получить размеры элемента
 * @param x - HTML элемент
 */
export const getSize = (x: HTMLElement): IO.IO<Readonly<Size>> => () => {
    const { width, height } = x.getBoundingClientRect();
    return { width, height };
}


/**
 * Получить координаты элемента относительно документа
 * @param x - HTML элемент
 */
export const getDocumentPosition = (env: Windowable) => 
    (x: HTMLElement): IO.IO<Readonly<Position>> =>
        pipe(
            sequenceT(IO.io)(
                getScreenPosition(x), 
                getScroll(env),
            ),
            IO.map(([screen, scroll]) => {
                const top = screen.top + scroll.top;
                const left = screen.left + scroll.left;

                return { top, left };
            }),
        );


/**
 * Уведомить в консоль о возможной ошибке и передать это в окружение.
 */
export const prepareError = (env: Errorable) => 
    (err: Error) => 
        () => {
            console.error(err.message);
            env.exception();
        }


export const safeRefHandle: <T extends HTMLElement>(fn: (el: T) => void) => (ref: React.RefObject<T>) => IO.IO<void> = 
    (fn) => (ref) => 
        pipe(
            ref.current,
            O.fromNullable,
            O.fold(
                () => () => {},
                (el) => () => fn(el),
            )
        );

export const safeFocus = safeRefHandle(el => el.focus());
export const safeBlur = safeRefHandle(el => el.blur());


// TODO: nned refactoring!
export const isHTMLElement = is(HTMLElement);
export const isWindow = is(Window);


export const isScrollable = (x: HTMLElement) => {
    const { overflow } = window.getComputedStyle(x);
    const one = x.clientWidth !== x.scrollWidth || x.clientHeight !== x.scrollHeight;
    const two = overflow.includes('auto') || overflow.includes('scroll');

    return one && two;
}

export const isBody = (x: HTMLElement) => {
    return x === document.body;
}
